import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { CardImage, CardMedia } from "@/components/CardImage";
import { ConditionBadge, Tag, StatusBadge } from "@/components/Badges";
import { Loading, ErrorState } from "@/components/States";
import { ProposalComposer } from "@/components/ProposalComposer";
import { ChatPanel, DeliveryPanel, PaymentPanel } from "@/components/TradePanels";
import { OmniShippingPanel } from "@/components/shipping/OmniShippingPanel";
import { ReputationPanel } from "@/components/reputation/ReputationPanel";
import { DisputeDialog } from "@/components/reputation/DisputeDialog";
import { CashFields, cashPayload, cashLine, euro } from "@/components/CashFields";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, Repeat, Copy, MessageCircle, Star, Info, AlertTriangle, History, Euro, Truck } from "lucide-react";
import { toast } from "sonner";

function ItemGrid({ items }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-lg border border-[#2A3655] bg-[#0B1020] p-1.5" data-testid="proposal-item">
          <CardMedia photos={it.photos} card={it.card} />
          <p className="mt-1 line-clamp-1 px-0.5 text-xs font-semibold text-[#F8FAFC]">{it.card?.name}</p>
          <div className="flex flex-wrap gap-1 px-0.5 pb-0.5"><ConditionBadge value={it.condition} /><Tag>{it.language}</Tag><Tag tone="#94A3B8">×{it.quantity}</Tag></div>
        </div>
      ))}
    </div>
  );
}

const DELIVERY_LABEL = { shipping: "Spedizione", in_person: "Incontro di persona" };

export default function ProposalDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [contact, setContact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [counterOpen, setCounterOpen] = useState(false);
  const [method, setMethod] = useState("telegram");
  const [value, setValue] = useState("");

  const load = () => { setError(null); api.get(`/proposals/${id}`).then((r) => setP(r.data)).catch(() => setError("Trattativa non trovata.")); };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (p && ["accettata", "completata", "contestata"].includes(p.status)) {
      api.get(`/proposals/${id}/contact`).then((r) => setContact(r.data.contact)).catch(() => setContact(null));
    }
  }, [p, id]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!p) return <Loading />;

  const isReceiver = user.id === p.to_user_id;
  const isSender = user.id === p.from_user_id;
  const other = isReceiver ? p.from_user : p.to_user;
  const nameFrom = p.from_user?.username ? `@${p.from_user.username}` : "Mittente";
  const nameTo = p.to_user?.username ? `@${p.to_user.username}` : "Destinatario";

  const act = async (fn) => { setBusy(true); try { await fn(); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const doAccept = async () => {
    if (!value.trim()) { toast.error("Indica un recapito da condividere."); return; }
    await act(async () => { await api.post(`/proposals/${id}/accept`, { contact_method: method, contact_value: value }); toast.success("Proposta accettata. Carte riservate."); setAcceptOpen(false); });
  };

  const Agreement = (
    <div>
      <div className="flex items-start gap-2 rounded-lg border border-[#2A3655] bg-[#1C2640] p-3 text-sm text-[#CBD5E1]"><Info className="mt-0.5 h-4 w-4 shrink-0 text-[#2DD4BF]" />Solo l'ultima versione attiva è accettabile e solo la controparte può accettarla. Lo scambio è carta contro carta; il conguaglio è denaro facoltativo in una sola direzione.</div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-[#2A3655] bg-[#141B2D] p-4">
          <h3 className="mb-3 font-display font-semibold text-[#2DD4BF]">{isReceiver ? `${nameFrom} offre` : "Tu offri"}</h3>
          <ItemGrid items={p.offered} />
        </div>
        <div className="rounded-2xl border border-[#2A3655] bg-[#141B2D] p-4">
          <h3 className="mb-3 font-display font-semibold text-[#7C3AED]">{isReceiver ? "In cambio chiede le tue" : `Chiedi a ${nameTo}`}</h3>
          <ItemGrid items={p.requested} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-1.5 text-sm text-[#F59E0B]" data-testid="cash-display"><Euro className="h-4 w-4" />{cashLine(p, nameFrom, nameTo)}</span>
        {p.delivery_method && <span className="inline-flex items-center gap-1.5 rounded-lg border border-[#2A3655] px-3 py-1.5 text-sm text-[#CBD5E1]"><Truck className="h-4 w-4 text-[#2DD4BF]" />Consegna proposta: {DELIVERY_LABEL[p.delivery_method]}</span>}
      </div>

      {p.note && <p className="mt-3 rounded-lg border border-[#2A3655] bg-[#0B1020] p-3 text-sm text-[#CBD5E1]"><b className="text-[#F8FAFC]">Nota:</b> {p.note}</p>}

      {contact && (
        <div className="mt-4 rounded-2xl border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 p-4" data-testid="contact-box">
          <p className="flex items-center gap-2 font-display font-semibold text-[#2DD4BF]"><MessageCircle className="h-4 w-4" />Recapito della controparte</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="rounded bg-[#0B1020] px-3 py-1.5 text-sm text-[#F8FAFC]" data-testid="contact-value-display">{contact.method === "telegram" ? "Telegram: " : contact.method === "discord" ? "Discord: " : "Email: "}{contact.value}</code>
            <button onClick={() => { navigator.clipboard.writeText(contact.value); toast.success("Recapito copiato."); }} data-testid="copy-contact" className="rounded-lg border border-[#2A3655] p-2 text-[#CBD5E1] hover:bg-[#1C2640]"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {p.status === "bozza" && isSender && <p className="text-sm text-[#94A3B8]">Questa è una bozza: non è ancora stata inviata alla controparte.</p>}
        {p.status === "inviata" && isReceiver && (
          <>
            <Button onClick={() => setAcceptOpen(true)} data-testid="accept-proposal" className="bg-[#10B981] hover:bg-[#0e9e6e]"><Check className="mr-2 h-4 w-4" />Accetta</Button>
            <Button onClick={() => setCounterOpen(true)} data-testid="counter-proposal" variant="outline" className="border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]"><Repeat className="mr-2 h-4 w-4" />Controproponi</Button>
            <Button onClick={() => act(() => api.post(`/proposals/${id}/reject`))} disabled={busy} data-testid="reject-proposal" variant="outline" className="border-[#EF4444]/40 bg-transparent text-[#EF4444] hover:bg-[#EF4444]/10"><X className="mr-2 h-4 w-4" />Rifiuta</Button>
          </>
        )}
        {p.status === "inviata" && isSender && (
          <Button onClick={() => act(() => api.post(`/proposals/${id}/cancel`))} disabled={busy} data-testid="cancel-proposal" variant="outline" className="border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]">Ritira proposta</Button>
        )}
        {p.status === "accettata" && (
          <>
            <Button onClick={() => act(async () => { const r = await api.post(`/proposals/${id}/complete`); toast.success(r.data.completed ? "Scambio completato!" : "Conformità confermata. In attesa della controparte."); })} disabled={busy} data-testid="complete-proposal" className="bg-[#10B981] hover:bg-[#0e9e6e]"><Check className="mr-2 h-4 w-4" />Conferma conformità</Button>
            {!p.cancel_requested_by && <Button onClick={() => act(() => api.post(`/proposals/${id}/cancel-request`))} disabled={busy} data-testid="cancel-request" variant="outline" className="border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]">Richiedi annullamento</Button>}
            {p.cancel_requested_by && p.cancel_requested_by !== user.id && <Button onClick={() => act(() => api.post(`/proposals/${id}/confirm-cancel`))} disabled={busy} data-testid="confirm-cancel" variant="outline" className="border-[#F59E0B]/40 bg-transparent text-[#F59E0B] hover:bg-[#F59E0B]/10">Conferma annullamento</Button>}
            <DisputeDialog pid={id} onDone={load} />
          </>
        )}
      </div>

      {p.status !== "accettata" && <ReputationPanel pid={id} status={p.status} otherUsername={other?.username} onChanged={load} />}

      {p.status === "accettata" && (
        <div className="mt-3 flex items-center gap-2 text-sm text-[#CBD5E1]">
          <span className={p.from_confirmed ? "text-[#10B981]" : "text-[#94A3B8]"}>{nameFrom} {p.from_confirmed ? "✓ conforme" : "in attesa"}</span> ·
          <span className={p.to_confirmed ? "text-[#10B981]" : "text-[#94A3B8]"}>{nameTo} {p.to_confirmed ? "✓ conforme" : "in attesa"}</span>
        </div>
      )}
    </div>
  );

  const Activity = (
    <div className="rounded-2xl border border-[#2A3655] bg-[#141B2D] p-4">
      <h3 className="flex items-center gap-2 font-display font-semibold text-[#F8FAFC]"><History className="h-4 w-4 text-[#2DD4BF]" />Storico</h3>
      <div className="mt-2 space-y-1">
        {(p.history || []).map((h, i) => <p key={i} className="text-xs text-[#94A3B8]" data-testid="history-event">{new Date(h.at).toLocaleString("it-IT")} — {h.event}</p>)}
        {p.parent_id && <Link to={`/proposte/${p.parent_id}`} className="text-xs text-[#2DD4BF] hover:underline">↳ Vedi la versione precedente</Link>}
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex items-center justify-between">
        <Link to="/proposte" className="text-sm text-[#2DD4BF] hover:underline">← Le mie trattative</Link>
        <StatusBadge status={p.status} />
      </div>
      <h1 className="mt-2 font-display text-2xl font-bold text-[#F8FAFC]">Stanza dello scambio con <Link to={`/collezionista/${other?.username}`} className="text-[#2DD4BF] hover:underline">@{other?.username}</Link></h1>
      <p className="mt-1 text-xs text-[#94A3B8]">Codice {p.id.slice(0, 8)} · versione {p.version} · creata il {new Date(p.created_at).toLocaleString("it-IT")}</p>

      <Tabs defaultValue="accordo" className="mt-4">
        <TabsList className="flex h-auto w-full justify-start overflow-x-auto bg-[#141B2D] border border-[#2A3655] sm:w-auto sm:inline-flex">
          <TabsTrigger value="accordo" data-testid="tab-accordo" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Accordo</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Chat</TabsTrigger>
          <TabsTrigger value="consegna" data-testid="tab-consegna" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Consegna</TabsTrigger>
          {p.cash_cents > 0 && <TabsTrigger value="pagamento" data-testid="tab-pagamento" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Pagamento</TabsTrigger>}
          <TabsTrigger value="spedizione" data-testid="tab-spedizione" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Spedizione</TabsTrigger>
          <TabsTrigger value="attivita" data-testid="tab-attivita" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Attività</TabsTrigger>
        </TabsList>
        <TabsContent value="accordo" className="mt-4">{Agreement}</TabsContent>
        <TabsContent value="chat" className="mt-4"><ChatPanel pid={id} status={p.status} /></TabsContent>
        <TabsContent value="consegna" className="mt-4"><DeliveryPanel pid={id} status={p.status} defaultMethod={p.delivery_method} proposal={p} myId={user.id} onAction={load} /></TabsContent>
        {p.cash_cents > 0 && <TabsContent value="pagamento" className="mt-4"><PaymentPanel pid={id} status={p.status} myId={user.id} /></TabsContent>}
        <TabsContent value="spedizione" className="mt-4"><OmniShippingPanel pid={id} status={p.status} /></TabsContent>
        <TabsContent value="attivita" className="mt-4">{Activity}</TabsContent>
      </Tabs>

      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
          <DialogHeader><DialogTitle className="font-display">Accetta lo scambio</DialogTitle><DialogDescription className="sr-only">Conferma l'accettazione e scegli un recapito da condividere.</DialogDescription></DialogHeader>
          <p className="text-sm text-[#CBD5E1]">Confermando riservi le carte di entrambi. Scegli un recapito da condividere con @{other?.username}.</p>
          {!!p.cash_cents && <p className="rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-2 text-sm text-[#F59E0B]">{cashLine(p, nameFrom, nameTo)} Il pagamento del conguaglio si effettua con carta nella scheda «Pagamento».</p>}
          <div className="grid grid-cols-[160px_1fr] gap-2">
            <div><Label className="text-[#CBD5E1]">Recapito</Label><Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="accept-method" className="bg-[#0B1020] border-[#2A3655]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]"><SelectItem value="telegram">Username Telegram</SelectItem><SelectItem value="email">Email di contatto</SelectItem></SelectContent></Select></div>
            <div><Label className="text-[#CBD5E1]">{method === "telegram" ? "Username Telegram" : "Email di contatto"}</Label><Input data-testid="accept-contact-value" value={value} onChange={(e) => setValue(e.target.value)} className="bg-[#0B1020] border-[#2A3655]" /></div>
          </div>
          <DialogFooter><Button onClick={doAccept} disabled={busy} data-testid="confirm-accept" className="bg-[#10B981] hover:bg-[#0e9e6e]">Conferma accettazione</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {counterOpen && <CounterComposer id={id} otherUsername={other?.username} onClose={() => setCounterOpen(false)} onDone={load} />}

    </div>
  );
}

function CounterComposer({ id, otherUsername, onClose, onDone }) {
  const [open, setOpen] = useState(true);
  const [mine, setMine] = useState([]);
  const [theirs, setTheirs] = useState([]);
  const [give, setGive] = useState({});
  const [receive, setReceive] = useState({});
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("telegram");
  const [value, setValue] = useState("");
  const [cash, setCash] = useState({ option: "none", amount: "" });
  const [delivery, setDelivery] = useState("");
  const [acceptsCash, setAcceptsCash] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/availabilities/mine").then((r) => setMine(r.data.filter((a) => a.available_for_trade && !a.card?.is_draft && a.available_qty > 0)));
    api.get(`/profiles/${otherUsername}`).then((r) => { setTheirs(r.data.cards); setAcceptsCash(r.data.accept_cash_adjustment !== false); }).catch(() => setTheirs([]));
  }, [otherUsername]);

  const offered = Object.entries(give).filter(([, q]) => q > 0);
  const requested = Object.entries(receive).filter(([, q]) => q > 0);
  const close = (v) => { setOpen(v); if (!v) onClose(); };

  const send = async () => {
    if (!offered.length || !requested.length) { toast.error("Seleziona almeno una carta per parte."); return; }
    if (!value.trim()) { toast.error("Indica un recapito."); return; }
    setSending(true);
    try {
      await api.post(`/proposals/${id}/counter`, {
        offered: offered.map(([availability_id, quantity]) => ({ availability_id, quantity: Number(quantity) })),
        requested: requested.map(([availability_id, quantity]) => ({ availability_id, quantity: Number(quantity) })),
        note, contact_method: method, contact_value: value, ...cashPayload(cash), delivery_method: delivery || null,
      });
      toast.success("Controproposta inviata (nuova versione). L'originale è superata.");
      close(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const Row = ({ item, sel, setSel }) => {
    const checked = sel[item.id] > 0;
    return (
      <label className={`flex items-center gap-2 rounded-lg border p-2 ${checked ? "border-[#2DD4BF] bg-[#1C2640]" : "border-[#2A3655]"}`}>
        <input type="checkbox" checked={checked} onChange={(e) => setSel((s) => ({ ...s, [item.id]: e.target.checked ? 1 : 0 }))} className="h-4 w-4 accent-[#7C3AED]" />
        <div className="h-10 w-8"><CardImage path={(item.photos || [])[0]} card={item.card} className="h-full w-full" /></div>
        <span className="truncate text-sm text-[#F8FAFC]">{item.card?.name}</span>
      </label>
    );
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#141B2D] border-[#2A3655] text-[#F8FAFC] sm:max-w-2xl" data-testid="counter-composer">
        <DialogHeader><DialogTitle className="font-display">Controproposta a @{otherUsername}</DialogTitle><DialogDescription className="sr-only">Crea una nuova versione della proposta.</DialogDescription></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><p className="mb-2 text-sm font-semibold text-[#2DD4BF]">TU OFFRI</p><div className="space-y-2">{mine.map((a) => <Row key={a.id} item={a} sel={give} setSel={setGive} />)}</div></div>
          <div><p className="mb-2 text-sm font-semibold text-[#7C3AED]">TU CHIEDI</p><div className="space-y-2">{theirs.map((a) => <Row key={a.id} item={a} sel={receive} setSel={setReceive} />)}</div></div>
        </div>
        <CashFields cash={cash} setCash={setCash} delivery={delivery} setDelivery={setDelivery} disabledCash={!acceptsCash} />
        <div><Label className="text-[#CBD5E1]">Nota</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#0B1020] border-[#2A3655]" /></div>
        <div className="grid grid-cols-[160px_1fr] gap-2">
          <div><Label className="text-[#CBD5E1]">Recapito</Label><Select value={method} onValueChange={setMethod}><SelectTrigger className="bg-[#0B1020] border-[#2A3655]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]"><SelectItem value="telegram">Telegram</SelectItem><SelectItem value="email">Email</SelectItem></SelectContent></Select></div>
          <div><Label className="text-[#CBD5E1]">Il tuo recapito</Label><Input data-testid="counter-contact" value={value} onChange={(e) => setValue(e.target.value)} className="bg-[#0B1020] border-[#2A3655]" /></div>
        </div>
        <DialogFooter><Button onClick={send} disabled={sending} data-testid="send-counter" className="bg-[#7C3AED] hover:bg-[#6D28D9]">{sending ? "Invio…" : "Invia controproposta"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
